import { Injectable, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { OrderRepository } from '../local-db/repositries/order-repository';
import { RegionRepository } from '../local-db/repositries/region-repository';
import { WarehouseRepository } from '../local-db/repositries/warehouse-repository';
import { MotiveRepository } from '../local-db/repositries/motive-repository';
import { EstablishmentRepository } from '../local-db/repositries/establishment-repository';
import { ApproverRepository } from '../local-db/repositries/approver-repository';
import { ProductRepository } from '../local-db/repositries/product-repository';
import { StockRepository } from '../local-db/repositries/stock-repository';
import { UserRepository } from '../local-db/repositries/user-repository';
import { Order } from '../entities/order';
import { Region } from '../entities/region';
import { Warehouse } from '../entities/warehouse';
import { Motive } from '../entities/motive';
import { Establishment } from '../entities/establishment';
import { Approver } from '../entities/approver';
import { Product } from '../entities/product';
import { Stock } from '../entities/stock';
import { User } from '../entities/user';
import { UserRegionRepository } from '../local-db/repositries/user-region-repository';
import { OrderService } from 'src/app/proxy/orders';
import { OrderDto, UpdateOrderDto } from 'src/app/proxy/orders/models';
import { SyncDataRepository } from '../local-db/repositries/sync-data-repository';
import { ToasterService } from '@abp/ng.theme.shared';
import { SyncLogRepository } from '../local-db/repositries/sync-log-repository';
import { ConnectionService } from './connection-service';
import { OrderState } from 'src/app/proxy/orders/enums';
import { LocalizationService, PermissionService } from '@abp/ng.core';
import { SynchronizationStep, SynchronizationStepDefinition } from './dtos/synchronization-step.dto';
import { RegionsService } from 'src/app/proxy/regions';
import { WarehousesService } from 'src/app/proxy/warehouses';
import { MotivesService } from 'src/app/proxy/motives';
import { EstablishmentsService } from 'src/app/proxy/establishments';
import { ApproversService } from 'src/app/proxy/approvers';
import { ProductsService } from 'src/app/proxy/products';
import { StocksService } from 'src/app/proxy/stocks';
import { UserService } from 'src/app/proxy/users';
import { BioforestService } from 'src/app/proxy/bioforest';
import { SCMPermissions } from '../AppConsts';

@Injectable({ providedIn: 'root' })
export class SynchronizationManagerService {
  readonly syncSteps = signal<SynchronizationStep[]>([]);
  readonly isSyncInProgress = signal(false);

  private readonly stepDefinitions: ReadonlyArray<SynchronizationStepDefinition> = [
    { key: 'orders', localizationKey: '::Orders' },
    { key: 'regions', localizationKey: '::Regions' },
    { key: 'userRegions', localizationKey: '::UserRegions' },
    { key: 'warehouses', localizationKey: '::Warehouses' },
    { key: 'motives', localizationKey: '::Motives' },
    { key: 'establishments', localizationKey: '::Establishments' },
    { key: 'approvers', localizationKey: '::Approvers' },
    { key: 'products', localizationKey: '::Products' },
    { key: 'stocks', localizationKey: '::Stocks' },
  ];

  constructor(
    private readonly connectionService: ConnectionService,
    private readonly userService: UserService,
    private readonly orderService: OrderService,
    private readonly regionsService: RegionsService,
    private readonly warehousesService: WarehousesService,
    private readonly motivesService: MotivesService,
    private readonly establishmentsService: EstablishmentsService,
    private readonly approversService: ApproversService,
    private readonly productsService: ProductsService,
    private readonly stocksService: StocksService,
    private readonly bioforestService: BioforestService,
    private readonly orderRepository: OrderRepository,
    private readonly regionRepository: RegionRepository,
    private readonly warehouseRepository: WarehouseRepository,
    private readonly motiveRepository: MotiveRepository,
    private readonly establishmentRepository: EstablishmentRepository,
    private readonly approverRepository: ApproverRepository,
    private readonly productRepository: ProductRepository,
    private readonly stockRepository: StockRepository,
    private readonly userRepository: UserRepository,
    private readonly userRegionRepository: UserRegionRepository,
    private readonly syncDataRepository: SyncDataRepository,
    private readonly syncLogRepository: SyncLogRepository,
    private readonly toaster: ToasterService,
    private readonly localization: LocalizationService,
    private readonly permissionService: PermissionService,
  ) {}

  async syncAll(email: string): Promise<void> {
    if (!this.canSynchronize) {
      return;
    }

    if (this.isSyncInProgress()) {
      this.toaster.warn(
        `${this.localization.instant('::Synchronization:AlreadyInProgress')}`
      );
      return;
    }

    await this.syncPendingOrders();
    this.syncSteps.set(this.stepDefinitions.map(step => ({
      key: step.key,
      label: this.getLocalizedStepLabel(step),
      status: 'pending',
    })));
    this.isSyncInProgress.set(true);

    try {
      const failedSteps: string[] = [];
      let bioforestUrl: string | undefined;

      await this.runSyncStep('orders', failedSteps, async () => {
        const syncResult = await firstValueFrom(
          this.orderService.getOrders({ skipHandleError: true })
        );

        if (!syncResult) {
          return;
        }

        const orders = syncResult.map(dto => new Order(dto));
        await this.orderRepository.upsertRemoteBulk(orders);
      });

      await this.runSyncStep('regions', failedSteps, async () => {
        const syncResult = await firstValueFrom(
          this.regionsService.getRegions({ skipHandleError: true })
        );

        if (!syncResult) {
          return;
        }

        const regions = syncResult.map(dto => new Region(dto));
        await this.regionRepository.upsertBulk(regions);
      });

      await this.runSyncStep('userRegions', failedSteps, async () => {
        const syncResult = await firstValueFrom(
          this.userService.getUserRegionsByEmail(email, { skipHandleError: true })
        );

        if (!syncResult) {
          return;
        }

        const userRegions = syncResult.map(dto => new Region(dto));
        await this.userRegionRepository.clear();
        await this.userRegionRepository.upsertBulk(userRegions);
      });

      await this.runSyncStep('warehouses', failedSteps, async () => {
        const syncResult = await firstValueFrom(
          this.warehousesService.getWarehouses({ skipHandleError: true })
        );

        if (!syncResult) {
          return;
        }

        const warehouses = syncResult.map(dto => new Warehouse(dto));
        await this.warehouseRepository.upsertBulk(warehouses);
      });

      await this.runSyncStep('motives', failedSteps, async () => {
        const syncResult = await firstValueFrom(
          this.motivesService.getMotives({ skipHandleError: true })
        );

        if (!syncResult) {
          return;
        }

        const motives = syncResult.map(dto => new Motive(dto));
        await this.motiveRepository.upsertBulk(motives);
      });

      await this.runSyncStep('establishments', failedSteps, async () => {
        const syncResult = await firstValueFrom(
          this.establishmentsService.getEstablishments({ skipHandleError: true })
        );

        if (!syncResult) {
          return;
        }

        const establishments = syncResult.map(dto => new Establishment(dto));
        await this.establishmentRepository.upsertBulk(establishments);
      });

      await this.runSyncStep('approvers', failedSteps, async () => {
        const syncResult = await firstValueFrom(
          this.approversService.getApprovers({ skipHandleError: true })
        );

        if (!syncResult) {
          return;
        }

        const approvers = syncResult.map(dto => new Approver(dto));
        await this.approverRepository.upsertBulk(approvers);
      });

      await this.runSyncStep('products', failedSteps, async () => {
        const syncResult = await firstValueFrom(
          this.productsService.getProducts({ skipHandleError: true })
        );

        if (!syncResult) {
          return;
        }

        const products = syncResult.map(dto => new Product(dto));
        await this.productRepository.clear();
        await this.productRepository.upsertBulk(products);
      });

      await this.runSyncStep('stocks', failedSteps, async () => {
        const syncResult = await firstValueFrom(
          this.stocksService.getStocks({ skipHandleError: true })
        );

        if (!syncResult) {
          return;
        }

        const stocks = syncResult.map(dto => new Stock(dto));
        await this.stockRepository.upsertBulk(stocks);
      });

      await this.runSyncStep('bioforestEnvironment', failedSteps, async () => {
        const syncResult = await firstValueFrom(
          this.bioforestService.getBioforestEnvironment({ skipHandleError: true })
        );

        bioforestUrl = syncResult?.url;
      });

      const syncDataToSave: { lastSyncAt: string; bioforestUrl?: string } = {
        lastSyncAt: new Date().toISOString(),
      };

      if (bioforestUrl !== undefined) {
        syncDataToSave.bioforestUrl = bioforestUrl;
      }

      await this.syncDataRepository.save(syncDataToSave);

      if (failedSteps.length > 0) {
        this.toaster.warn(
          `${this.localization.instant('::Synchronization:CompletedWithErrors')}: ${failedSteps.join(', ')}`
        );
      }
    } finally {
      this.isSyncInProgress.set(false);
    }
  }

  async syncOrderById(orderId: string): Promise<void> {
    if (!this.canSynchronize) {
      return;
    }

    try {
      await this.syncPendingOrderById(orderId);
      const order = await this.orderRepository.getByIdentifier(orderId);
      if (!order?.id) {
        return;
      }
      const result = await firstValueFrom(
        this.orderService.getById(order.id,  { skipHandleError: true })
      );
      if (result) {
        const syncedOrder = new Order({
          ...result,
          localId: order.localId,
          pendingSend: order.pendingSend,
        });
        await this.orderRepository.upsertRemote(syncedOrder);
      }
    } catch {
      this.toaster.warn('::ErrorSyncOrder')
    }
  }

  private async syncPendingOrderById(orderId: string): Promise<void> {
    const pendingOrder = await this.orderRepository.getByIdentifier(orderId);

    if (!pendingOrder?.pendingSend) {
      return;
    }

    await this.sendPendingOrder(pendingOrder);
  }

  private get canSynchronize(): boolean {
    return this.permissionService.getGrantedPolicy(SCMPermissions.Sync);
  }

  private async syncPendingOrders(): Promise<void> {
    const pendingOrders = await this.orderRepository.getPendingSendOrders();

    for (const pendingOrder of pendingOrders) {
      await this.sendPendingOrder(pendingOrder);
    }
  }

  private async runSyncStep(
    stepName: string,
    failedSteps: string[],
    action: () => Promise<void>
  ): Promise<void> {
    this.updateStep(stepName, {
      status: 'running',
    });

    try {
      await action();
      this.updateStep(stepName, {
        status: 'success',
      });
    } catch {
      failedSteps.push(stepName);
      this.updateStep(stepName, {
        status: 'error',
      });
    }
  }

  private updateStep(
    stepName: string,
    partialStep: Partial<SynchronizationStep>
  ): void {
    this.syncSteps.update(steps =>
      steps.map(step =>
        step.key === stepName
          ? { ...step, ...partialStep }
          : step
      )
    );
  }

  private getLocalizedStepLabel(step: SynchronizationStepDefinition): string {
    return this.localization.instant(step.localizationKey);
  }

  private async buildOrderUpdatePayload(order: Order): Promise<OrderDto> {
    const payload: UpdateOrderDto = { ...order };
    const originWarehouse = await this.warehouseRepository.getById(order.originWarehouseId);
    const destinationWarehouse = await this.warehouseRepository.getById(order.destinationWarehouseId);

    payload.originWarehouseManagerId = originWarehouse?.warehouseManagerId;
    payload.destinationWarehouseManagerId = destinationWarehouse?.warehouseManagerId;

    return payload;
  }

  private async sendPendingOrder(order: Order): Promise<void> {
    try {
      const updatedOrderPayload = await this.buildOrderUpdatePayload(order);

      if (this.connectionService.online()) {
        let result;
        if (order.orderState == OrderState.New) {
          result = await firstValueFrom(
            this.orderService.createByOrder(updatedOrderPayload,  { skipHandleError: true })
          );
        } else {
          result = await firstValueFrom(
            this.orderService.updateByIdAndOrder(order.id, updatedOrderPayload,  { skipHandleError: true })
          );
        }
        const syncedOrder = new Order({
          ...result,
          localId: order.localId,
          pendingSend: false,
        });
        syncedOrder.pendingSend = false;
        await this.orderRepository.upsertRemote(syncedOrder);
      } else {
        this.toaster.warn('::UnableToPostPendingOrder');
      }
    } catch (error) {
      this.toaster.error(error.error.error.message);
      await this.syncLogRepository.create({
        orderId: order.localId,
        errorMessage: error.error.error.message,
        orderState: order.orderState,
        createdAt: new Date().toISOString(),
      });
    }
  }
}
